<template>
  <div class="signature-container">
    <div class="page-header">
      <div class="header-content">
        <i class="fa fa-file-pdf-o header-icon"></i>
        <h2 class="header-title">PDF 签名工具</h2>
      </div>
    </div>

    <div class="main-content">
      <!-- 左侧：PDF预览 -->
      <div class="pdf-panel">
        <div class="panel-header">
          <span class="panel-title"><i class="fa fa-file-pdf-o"></i> PDF 预览</span>
          <div class="pdf-actions">
            <button class="el-button el-button--primary el-button--mini" @click="importPdf">
              <i class="el-icon-upload2"></i>
              {{ pdfFileName ? '更换文件' : '导入PDF' }}
            </button>
            <button 
              class="el-button el-button--success el-button--mini"
              :disabled="!pdfFileName || signatures.length === 0"
              @click="saveSignedPdf"
            >
              <i class="el-icon-download"></i>
              另存为
            </button>
          </div>
        </div>

        <div class="pdf-viewer" ref="pdfViewer">
          <div v-if="!pdfFileName" class="pdf-empty">
            <i class="fa fa-file-pdf-o empty-icon"></i>
            <p>请导入 PDF 文件</p>
            <p class="hint">点击上方"导入PDF"按钮选择文件</p>
          </div>
          <div v-else class="pdf-pages">
            <div 
              v-for="index in pageCount" 
              :key="index - 1"
              class="pdf-page-wrapper"
              :class="{active: targetPage === (index - 1)}"
            >
              <div class="page-number">第 {{ index }} 页</div>
              <div class="page-canvas-container">
                <canvas :id="'pdfCanvas' + (index - 1)" class="pdf-canvas"></canvas>
                <div class="signature-overlay">
                  <div 
                    v-for="sig in getSignaturesOnPage(index - 1)" 
                    :key="sig.id"
                    class="signature-item"
                    :style="{left: sig.x + 'px', top: sig.y + 'px', width: sig.width + 'px', height: sig.height + 'px'}"
                    @mousedown="startDrag($event, sig)"
                    @touchstart="startDrag($event, sig)"
                  >
                    <img :src="sig.dataUrl" class="sig-img" draggable="false" />
                    <div class="sig-tools">
                      <span class="sig-btn drag" title="拖动"><i class="fa fa-arrows"></i></span>
                      <span class="sig-btn delete" title="删除" @click.stop="removeSig(sig.id)"><i class="fa fa-trash"></i></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧：签名工具 -->
      <div class="tools-panel">
        <!-- 步骤指引 -->
        <div class="step-guide">
          <div class="step" :class="{active: currentStep >= 1, current: currentStep === 1}">
            <span class="step-num">1</span>
            <span class="step-text">导入PDF</span>
          </div>
          <div class="step-line"></div>
          <div class="step" :class="{active: currentStep >= 2, current: currentStep === 2}">
            <span class="step-num">2</span>
            <span class="step-text">绘制签名</span>
          </div>
          <div class="step-line"></div>
          <div class="step" :class="{active: currentStep >= 3, current: currentStep === 3}">
            <span class="step-num">3</span>
            <span class="step-text">保存</span>
          </div>
        </div>

        <!-- 签名画板 -->
        <div class="panel-section">
          <div class="section-title"><i class="fa fa-pencil-square"></i> 签名区域</div>
          
          <div v-if="isDrawing" class="draw-mode">
            <div class="draw-hint">
              <i class="fa fa-hand-pointer-o"></i>
              在下方使用鼠标或触摸屏签名
            </div>
            <div class="sign-canvas-wrap drawing">
              <vue-esign ref="vueEsignRef" :width="380" :height="200" :lineWidth="lineWidth" :lineColor="lineColor" />
            </div>
            <div class="draw-actions">
              <button class="el-button el-button--success el-button--small" @click="finishDraw">
                <i class="fa fa-check"></i> 完成
              </button>
              <button class="el-button el-button--default el-button--small" @click="clearCanvas">
                <i class="fa fa-eraser"></i> 重写
              </button>
            </div>
          </div>
          
          <div v-else class="preview-mode">
            <div class="sign-canvas-wrap" :class="{empty: !currentSignUrl}">
              <div v-if="!currentSignUrl" class="sign-placeholder">
                <i class="fa fa-edit"></i>
                <span>点击下方按钮开始签名</span>
              </div>
              <img v-else :src="currentSignUrl" class="sign-preview" />
            </div>
            
            <div class="sign-btns">
              <button class="el-button el-button--primary el-button--small" @click="startDrawing">
                <i class="fa fa-pencil"></i>
                {{ currentSignUrl ? '重新签名' : '开始签名' }}
              </button>
              <button v-if="currentSignUrl" class="el-button el-button--danger el-button--small" @click="clearSign">
                <i class="fa fa-trash-o"></i> 清空
              </button>
            </div>
          </div>
        </div>

        <!-- 添加到PDF -->
        <div class="panel-section" v-if="currentSignUrl && pdfFileName">
          <div class="section-title"><i class="fa fa-plus-circle"></i> 添加到PDF</div>
          <div class="add-form">
            <div class="add-hint-box">
              <i class="fa fa-check-circle"></i>
              <span>签名已就绪！</span>
            </div>
            <div class="page-select">
              <label>目标页面：</label>
              <select v-model="targetPage" class="el-input__inner">
                <option v-for="n in pageCount" :key="n" :value="n-1">第 {{ n }} 页</option>
              </select>
            </div>
            <button class="el-button el-button--primary add-btn" @click="addToPdf">
              <i class="fa fa-plus"></i> 添加签名到此页
            </button>
          </div>
        </div>

        <!-- 已添加签名 -->
        <div class="panel-section" v-if="signatures.length > 0">
          <div class="section-title">
            <i class="fa fa-list"></i> 已添加签名
            <span class="badge">{{ signatures.length }}</span>
          </div>
          <div class="sig-list">
            <div v-for="sig in signatures" :key="sig.id" class="sig-list-item">
              <span>第 {{ sig.page + 1 }} 页</span>
              <button class="el-button el-button--text el-button--mini danger" @click="removeSig(sig.id)">
                <i class="fa fa-trash"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- 画笔设置 -->
        <div class="panel-section">
          <div class="section-title"><i class="fa fa-palette"></i> 画笔设置</div>
          <div class="setting-row">
            <label>粗细：</label>
            <el-slider v-model="lineWidth" :min="1" :max="10" :show-tooltip="false"></el-slider>
            <span class="val">{{ lineWidth }}</span>
          </div>
          <div class="setting-row">
            <label>颜色：</label>
            <input type="color" v-model="lineColor" class="color-pick" />
          </div>
          <div class="quick-colors">
            <span v-for="c in quickColors" :key="c" class="quick-color" 
              :class="{active: lineColor === c}" :style="{background: c}" @click="lineColor = c"></span>
          </div>
        </div>
      </div>
    </div>

    <div class="status-bar" v-if="pdfFileName">
      <span><i class="fa fa-file-pdf-o"></i> {{ pdfFileName }}</span>
      <span><i class="fa fa-file"></i> {{ pageCount }} 页</span>
      <span><i class="fa fa-edit"></i> {{ signatures.length }} 个签名</span>
    </div>
  </div>
</template>

<script>
import vueEsign from 'vue-esign'
import pdfjsLib from 'pdfjs-dist'
import 'pdfjs-dist/build/pdf.worker.entry'
import { PDFDocument } from 'pdf-lib'

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsLib.pdfjsWorker

export default {
  name: 'Signature',
  components: { vueEsign },
  data() {
    return {
      pdfFileName: '',
      pdfDoc: null,
      pageCount: 0,
      pdfBuffer: null,
      signatures: [],
      sigId: 0,
      currentSignUrl: '',
      isDrawing: false,
      lineWidth: 3,
      lineColor: '#000000',
      quickColors: ['#000000', '#333333', '#0066cc', '#cc0000', '#006600', '#6600cc'],
      targetPage: 0,
      dragSig: null,
      dragOffX: 0,
      dragOffY: 0,
      currentStep: 1,
      fileInput: null,
      scale: 1.5
    }
  },
  methods: {
    // 导入PDF
    importPdf() {
      if (!this.fileInput) {
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = '.pdf'
        input.style.display = 'none'
        const self = this
        input.onchange = function(e) { self.onFileSelected(e) }
        document.body.appendChild(input)
        this.fileInput = input
      }
      this.fileInput.click()
    },
    
    // 文件选择
    onFileSelected(e) {
      const file = e.target.files[0]
      if (!file) return
      
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        this.$message.error('请选择PDF文件')
        return
      }
      
      this.pdfFileName = file.name
      this.signatures = []
      this.currentSignUrl = ''
      this.isDrawing = false
      this.currentStep = 2
      this.targetPage = 0  // 重置为第一页
      
      const reader = new FileReader()
      const self = this
      reader.onload = function(ev) {
        self.pdfBuffer = ev.target.result
        self.loadPdf()
      }
      reader.readAsArrayBuffer(file)
    },
    
    // 加载PDF
    async loadPdf() {
      if (!this.pdfBuffer) return
      
      try {
        this.$message.info('正在加载PDF...')
        this.pdfDoc = await pdfjsLib.getDocument({
          data: this.pdfBuffer,
          cMapUrl: 'https://unpkg.com/pdfjs-dist@2.0.943/cmaps/',
          cMapPacked: true
        }).promise
        
        this.pageCount = this.pdfDoc.numPages
        this.$message.success('加载成功，共 ' + this.pageCount + ' 页')
        
        setTimeout(() => this.renderPages(), 200)
      } catch (err) {
        console.error('PDF加载失败:', err)
        this.$message.error('PDF加载失败')
      }
    },
    
    // 渲染页面
    async renderPages() {
      if (!this.pdfDoc) return
      
      for (let i = 1; i <= this.pageCount; i++) {
        try {
          const page = await this.pdfDoc.getPage(i)
          const vp = page.getViewport(this.scale)
          
          const canvas = document.getElementById('pdfCanvas' + (i - 1))
          if (!canvas) {
            console.error('Canvas未找到: pdfCanvas' + (i - 1))
            continue
          }
          
          canvas.width = vp.width
          canvas.height = vp.height
          
          await page.render({
            canvasContext: canvas.getContext('2d'),
            viewport: vp
          }).promise
        } catch (e) {
          console.error('渲染页面失败:', e)
        }
      }
    },
    
    // 开始绘制
    startDrawing() {
      this.isDrawing = true
      this.$nextTick(() => {
        if (this.$refs.vueEsignRef) {
          this.$refs.vueEsignRef.reset()
        }
      })
    },
    
    // 完成绘制
    finishDraw() {
      try {
        // 查找签名画布
        const signWrap = document.querySelector('.sign-canvas-wrap.drawing')
        if (!signWrap) {
          this.$message.error('签名区域未找到')
          return
        }
        
        const canvas = signWrap.querySelector('canvas')
        if (!canvas) {
          this.$message.error('签名画布未找到')
          return
        }
        
        // 直接获取签名数据
        const dataUrl = canvas.toDataURL('image/png')
        
        if (dataUrl && dataUrl.startsWith('data:image/png') && dataUrl.length > 100) {
          this.currentSignUrl = dataUrl
          this.$message.success('签名已保存！')
        } else {
          this.$message.error('请先绘制签名')
        }
      } catch (err) {
        console.error('生成签名失败:', err)
        this.$message.error('签名生成失败: ' + err.message)
      }
      this.isDrawing = false
    },
    
    // 清空画布
    clearCanvas() {
      if (this.$refs.vueEsignRef) {
        this.$refs.vueEsignRef.reset()
      }
    },
    
    // 清空签名
    clearSign() {
      this.currentSignUrl = ''
    },
    
    // 添加到PDF
    addToPdf() {
      if (!this.currentSignUrl) {
        this.$message.warning('请先绘制签名')
        return
      }
      
      if (!this.pdfFileName) {
        this.$message.warning('请先导入PDF')
        return
      }
      
      const canvas = document.getElementById('pdfCanvas' + this.targetPage)
      if (!canvas) {
        this.$message.error('页面加载失败')
        return
      }
      
      // 计算签名尺寸（自适应canvas大小）
      const sigWidth = Math.min(200, canvas.width * 0.3)
      const sigHeight = sigWidth * 0.4
      
      // 默认放在页面下部（右下角）
      const sigX = canvas.width - sigWidth - 50
      const sigY = canvas.height - sigHeight - 50
      
      this.signatures.push({
        id: ++this.sigId,
        dataUrl: this.currentSignUrl,
        page: this.targetPage,
        x: sigX,
        y: sigY,
        width: sigWidth,
        height: sigHeight
      })
      
      this.$message.success('签名已添加到第 ' + (this.targetPage + 1) + ' 页，可拖动调整位置')
      this.currentSignUrl = ''
      this.isDrawing = false
      this.currentStep = 3
    },
    
    // 获取页面签名
    getSignaturesOnPage(pageIndex) {
      return this.signatures.filter(s => s.page === pageIndex)
    },
    
    // 开始拖动
    startDrag(e, sig) {
      e.preventDefault()
      this.dragSig = sig
      
      const touch = e.touches ? e.touches[0] : e
      this.dragOffX = touch.clientX - sig.x
      this.dragOffY = touch.clientY - sig.y
      
      document.addEventListener('mousemove', this.onDrag.bind(this))
      document.addEventListener('mouseup', this.stopDrag.bind(this))
      document.addEventListener('touchmove', this.onDrag.bind(this), {passive: false})
      document.addEventListener('touchend', this.stopDrag.bind(this))
    },
    
    // 拖动中
    onDrag(e) {
      if (!this.dragSig) return
      e.preventDefault()
      
      const touch = e.touches ? e.touches[0] : e
      const canvas = document.getElementById('pdfCanvas' + this.dragSig.page)
      if (!canvas) return
      
      const rect = canvas.getBoundingClientRect()
      let nx = touch.clientX - rect.left - this.dragOffX + rect.left
      let ny = touch.clientY - rect.top - this.dragOffY + rect.top
      
      nx = Math.max(0, Math.min(nx, canvas.width - this.dragSig.width))
      ny = Math.max(0, Math.min(ny, canvas.height - this.dragSig.height))
      
      this.dragSig.x = nx
      this.dragSig.y = ny
    },
    
    // 停止拖动
    stopDrag() {
      this.dragSig = null
      document.removeEventListener('mousemove', this.onDrag.bind(this))
      document.removeEventListener('mouseup', this.stopDrag.bind(this))
      document.removeEventListener('touchmove', this.onDrag.bind(this))
      document.removeEventListener('touchend', this.stopDrag.bind(this))
    },
    
    // 删除签名
    removeSig(id) {
      const idx = this.signatures.findIndex(s => s.id === id)
      if (idx > -1) {
        this.signatures.splice(idx, 1)
        this.$message.info('签名已删除')
      }
    },
    
    // 保存带签名的PDF
    async saveSignedPdf() {
      if (!this.pdfBuffer || this.signatures.length === 0) {
        this.$message.warning('请先添加签名')
        return
      }
      
      try {
        this.$message.info('正在生成PDF...')
        
        // 重新加载PDF
        const pdfDoc = await PDFDocument.load(this.pdfBuffer)
        const pages = pdfDoc.getPages()
        
        // 按页分组签名
        const byPage = {}
        this.signatures.forEach(sig => {
          if (!byPage[sig.page]) byPage[sig.page] = []
          byPage[sig.page].push(sig)
        })
        
        // 处理每一页
        for (const pg in byPage) {
          const pageIndex = parseInt(pg)
          const page = pages[pageIndex]
          const { width: pw, height: ph } = page.getSize()
          
          // 获取canvas
          const canvas = document.getElementById('pdfCanvas' + pg)
          if (!canvas) {
            console.error('Canvas未找到: pdfCanvas' + pg)
            continue
          }
          
          console.log(`页面 ${pg}: PDF尺寸 ${pw}x${ph}, Canvas尺寸 ${canvas.width}x${canvas.height}`)
          
          // 计算缩放比例
          const sx = pw / canvas.width
          const sy = ph / canvas.height
          
          // 处理每一页上的签名
          for (const sig of byPage[pg]) {
            try {
              // 重新创建签名图片，确保方向正确
              const img = await this.prepareSignatureImage(sig.dataUrl, pdfDoc)
              
              // 坐标转换
              const pdfX = sig.x * sx
              const pdfY = (canvas.height - sig.y - sig.height) * sy
              const pdfWidth = sig.width * sx
              const pdfHeight = sig.height * sy
              
              console.log(`签名: x=${sig.x}, y=${sig.y}`)
              console.log(`PDF签名: x=${pdfX.toFixed(1)}, y=${pdfY.toFixed(1)}`)
              
              page.drawImage(img, {
                x: pdfX,
                y: pdfY,
                width: pdfWidth,
                height: pdfHeight
              })
            } catch (err) {
              console.error('签名处理失败:', err)
            }
          }
        }
        
        // 保存PDF
        const pdfBytes = await pdfDoc.save()
        const blob = new Blob([pdfBytes], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = this.pdfFileName.replace('.pdf', '_signed.pdf')
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        
        this.$message.success('PDF已保存！')
      } catch (err) {
        console.error('保存失败:', err)
        this.$message.error('保存失败: ' + err.message)
      }
    },
    
    // 准备签名图片 - 确保方向正确
    async prepareSignatureImage(dataUrl, pdfDoc) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          // 创建新的canvas来翻转图片
          const canvas = document.createElement('canvas')
          canvas.width = img.width
          canvas.height = img.height
          const ctx = canvas.getContext('2d')
          
          // 填充白色背景
          ctx.fillStyle = '#ffffff'
          ctx.fillRect(0, 0, canvas.width, canvas.height)
          
          // 绘制图片（不翻转）
          ctx.drawImage(img, 0, 0)
          
          // 转换为字节数组
          const imgData = canvas.toDataURL('image/png')
          let base64 = imgData.split(',')[1]
          const binary = atob(base64)
          const bytes = new Uint8Array(binary.length)
          for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i)
          }
          
          // 嵌入PDF
          pdfDoc.embedPng(bytes).then(embeddedImg => {
            resolve(embeddedImg)
          }).catch(reject)
        }
        img.onerror = reject
        img.src = dataUrl
      })
    }
  }
}
</script>

<style scoped>
.signature-container {
  padding: 16px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
  min-height: 100vh;
  border-radius: 8px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  margin-bottom: 12px;
}

.header-content { display: flex; align-items: center; gap: 12px; }
.header-icon { font-size: 24px; }
.header-title { margin: 0; font-size: 20px; font-weight: 600; }

.main-content { 
  display: flex; 
  gap: 16px; 
  flex: 1;
  overflow: hidden;
}

/* PDF面板 */
.pdf-panel {
  flex: 1;
  min-width: 600px;
  min-height: 0;
  background: white;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f0f0;
  flex-shrink: 0;
}

.panel-title { font-size: 15px; font-weight: 600; color: #333; }
.panel-title i { margin-right: 8px; color: #e74d3d; }
.pdf-actions { display: flex; gap: 8px; }

.pdf-viewer {
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
  background: #525659;
  border-radius: 4px;
  padding: 16px;
}

.pdf-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #999;
}
.empty-icon { font-size: 72px; color: #ccc; margin-bottom: 16px; }
.hint { font-size: 13px; }

.pdf-pages { display: flex; flex-direction: column; align-items: center; gap: 10px; width: fit-content; }

.pdf-page-wrapper {
  position: relative;
  background: white;
  box-shadow: 0 2px 10px rgba(0,0,0,0.3);
  transition: box-shadow 0.3s;
  flex-shrink: 0;
}
.pdf-page-wrapper.active { box-shadow: 0 0 0 3px #409eff, 0 2px 15px rgba(64,158,255,0.4); }

.page-number {
  text-align: center;
  padding: 4px;
  background: #f5f5f5;
  font-size: 12px;
  color: #666;
  border-bottom: 1px solid #eee;
}

.page-canvas-container { position: relative; line-height: 0; }
.pdf-canvas { display: block; }

.signature-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
}

.signature-item {
  position: absolute;
  cursor: move;
  pointer-events: auto;
  user-select: none;
  touch-action: none;
}
.sig-img { width: 100%; height: 100%; object-fit: contain; pointer-events: none; }

.sig-tools {
  position: absolute;
  top: -28px;
  right: 0;
  display: flex;
  gap: 4px;
  opacity: 0;
  background: rgba(255,255,255,0.95);
  padding: 4px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  transition: opacity 0.2s;
}
.signature-item:hover .sig-tools { opacity: 1; }

.sig-btn {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  font-size: 11px;
}
.sig-btn:hover { background: #f0f0f0; }
.sig-btn.drag { color: #409eff; }
.sig-btn.delete { color: #f56c6c; }

/* 工具面板 */
.tools-panel { 
  width: 320px; 
  display: flex; 
  flex-direction: column; 
  gap: 12px; 
  overflow-y: auto;
  min-height: 0;
}

.step-guide {
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  flex-shrink: 0;
}
.step { display: flex; align-items: center; gap: 8px; opacity: 0.4; }
.step.active, .step.current { opacity: 1; }
.step.current .step-num { background: #409eff; color: white; }
.step-num {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ccc;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
}
.step-text { font-size: 13px; color: #333; }
.step-line { width: 30px; height: 2px; background: #ddd; margin: 0 8px; }

.panel-section {
  background: white;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  flex-shrink: 0;
}

.section-title {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 6px;
}
.section-title i { color: #667eea; }
.badge {
  background: #667eea;
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  margin-left: auto;
}

.sign-canvas-wrap {
  background: #fafafa;
  border: 2px dashed #d9d9d9;
  border-radius: 4px;
  overflow: hidden;
}
.sign-canvas-wrap.drawing { border-style: solid; border-color: #409eff; background: white; }
.sign-canvas-wrap.empty { min-height: 200px; display: flex; align-items: center; justify-content: center; }

.sign-placeholder { color: #ccc; text-align: center; padding: 40px; }
.sign-placeholder i { font-size: 40px; display: block; margin-bottom: 12px; }
.sign-placeholder span { font-size: 13px; }
.sign-preview { max-width: 100%; display: block; margin: 0 auto; }

.draw-hint {
  font-size: 12px;
  color: #409eff;
  text-align: center;
  padding: 8px;
  background: #ecf5ff;
  border-radius: 4px;
  margin-bottom: 10px;
}

.draw-actions, .sign-btns { display: flex; gap: 8px; margin-top: 10px; }

.add-form { display: flex; flex-direction: column; gap: 12px; }
.add-hint-box {
  background: #f0f9eb;
  border: 1px solid #e1f3d8;
  color: #67c23a;
  padding: 10px 12px;
  border-radius: 4px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-select { display: flex; align-items: center; gap: 10px; font-size: 13px; }
.page-select label { color: #666; }
.page-select select { flex: 1; height: 32px; border: 1px solid #dcdfe6; border-radius: 4px; padding: 0 10px; }
.add-btn { width: 100%; }

.sig-list { display: flex; flex-direction: column; gap: 6px; max-height: 120px; overflow-y: auto; }
.sig-list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 13px;
}
.danger { color: #f56c6c !important; }

.setting-row { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.setting-row label { font-size: 12px; color: #666; min-width: 40px; }
.setting-row .el-slider { flex: 1; }
.setting-row .val { font-size: 11px; color: #999; min-width: 20px; }

.color-pick { width: 36px; height: 28px; border: 1px solid #dcdfe6; border-radius: 4px; cursor: pointer; padding: 0; }

.quick-colors { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
.quick-color {
  width: 26px;
  height: 26px;
  border-radius: 4px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s;
}
.quick-color:hover { transform: scale(1.15); }
.quick-color.active { border-color: #667eea; }

.status-bar {
  display: flex;
  gap: 20px;
  margin-top: 12px;
  padding: 8px 12px;
  background: white;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  flex-shrink: 0;
}
.status-bar span { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #666; }
.status-bar i { color: #667eea; }

.el-button { display: inline-flex; align-items: center; gap: 4px; }
.el-slider__bar { background: linear-gradient(135deg, #667eea, #764ba2); }
.el-slider__button { border-color: #667eea; }

@media (max-width: 900px) {
  .main-content { flex-direction: column; }
  .pdf-panel { min-width: 100%; }
  .tools-panel { width: 100%; }
}
</style>



// WEBPACK FOOTER //
// src/views/Core/Signature.vue